/**
 * MODULE: _shared/frontend/utils/braznLinkedInExtensionAuth.ts
 * PURPOSE: Webapp → Chrome extension auth sync via `chrome.runtime.sendMessage` (pages cannot use `chrome.storage`).
 * DEPENDENCIES: `import.meta.env.VITE_BRAZN_LINKEDIN_EXTENSION_ID`; optional `chrome.runtime` on window.
 * PUBLIC_API: `parseLinkedInExtensionSyncCredentials`, `syncBraznLinkedInExtensionAuth`, `clearBraznLinkedInExtensionAuth`.
 * SIDE_EFFECTS: Sends external messages to the extension ID (no localStorage).
 * AGENT_SAFETY: 🟡 RESTRICTED (auth surface; keep parsers tested).
 * FILE_SIZE: 57 / 150
 * RECENT: 2026-04-13 — module header compliance (**`CHANGELOG` [2.2.472]**).
 */

type ChromeRuntime = {
  sendMessage?: (extensionId: string, message: unknown, responseCallback?: () => void) => void;
  lastError?: { message?: string };
};

function chromeRuntime(): ChromeRuntime | undefined {
  return (globalThis as { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
}

/**
 * Trim token and tenant for SYNC_AUTH. Returns null if either is empty after trim
 * (no message should be sent).
 */
export function parseLinkedInExtensionSyncCredentials(
  token: string,
  tenantId: string,
): { token: string; tenantId: string } | null {
  const t = token.trim();
  const tid = tenantId.trim();
  if (!t || !tid) return null;
  return { token: t, tenantId: tid };
}

export function syncBraznLinkedInExtensionAuth(token: string, tenantId: string): void {
  const extensionId = (import.meta.env.VITE_BRAZN_LINKEDIN_EXTENSION_ID as string | undefined)?.trim();
  const rt = chromeRuntime();
  if (!extensionId || !rt?.sendMessage) {
    return;
  }
  const creds = parseLinkedInExtensionSyncCredentials(token, tenantId);
  if (!creds) return;
  rt.sendMessage(extensionId, { type: 'SYNC_AUTH', ...creds }, () => {
    void chromeRuntime()?.lastError;
  });
}

export function clearBraznLinkedInExtensionAuth(): void {
  const extensionId = (import.meta.env.VITE_BRAZN_LINKEDIN_EXTENSION_ID as string | undefined)?.trim();
  const rt = chromeRuntime();
  if (!extensionId || !rt?.sendMessage) {
    return;
  }
  rt.sendMessage(extensionId, { type: 'CLEAR_AUTH' }, () => {
    void chromeRuntime()?.lastError;
  });
}
